import { paramsSerializer } from "@core/helpers"
import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"

export type GetCompareGraphByBuildingIdsResponse =
    | {
          graph: string
          periods: {
              date_from: string
              date_to: string
              indicators: ({ _id_building: string } & Record<
                  string,
                  Record<"sum" | "mean", number>
              >)[]
              total: {
                  data: Record<string, Record<"sum" | "mean", number>>
              }
          }[]
      }
    | { task_id: string }

export class DashboardEmService {
    /**
     * Récupère les indicateurs pour une liste de bâtiments sur une variabl selon
     * une période et une fréquence donnée
     * @param _id_buildings Liste des identifiants des bâtiments
     * @param variable La variable à récupérer au format "x_y_z.stat"
     * @param date_from La date de début au format "YYYY-MM-DD"
     * @param date_to  La date de fin au format "YYYY-MM-DD"
     * @param freq La fréquence de récupération des données
     * @returns
     */
    async getCompareGraphByBuildingIds(
        _id_buildings: string[],
        variable: string,
        periods: string[],
        freq: "hourly" | "daily",
        unit: string,
        format: string,
        ranking_order: string,
        {
            _id_user = "",
            async = false,
        }: {
            _id_user: string
            async: boolean
        } = { _id_user: "", async: false }
    ): Promise<GetCompareGraphByBuildingIdsResponse> {
        return handleServiceCall<GetCompareGraphByBuildingIdsResponse>(
            async () => {
                try {
                    const headers = {
                        "x-user-subscribe-id": _id_user,
                    } as Record<string, string | boolean>

                    if (async) {
                        headers["x-run-async"] = async
                    }

                    const { data } = await axios.post(
                        `/api/dapi/compare/buildings/graph`,
                        {
                            _id_buildings,
                            variable,
                            periods,
                            freq,
                            unit,
                            format,
                            ranking_order,
                        },
                        {
                            headers,
                        }
                    )

                    if (data?.data?.task_id) {
                        return { task_id: data.data.task_id }
                    }

                    return data
                } catch (error) {
                    // cast l'erreur en erreur axios
                    const axiosError = error as {
                        response: {
                            status: number
                            data: {
                                data: { task_id: string }
                                error: { code: string }
                            }
                        }
                    }

                    if (axiosError.response.status === 303) {
                        // si on lance en async on renvoie l'id de la tâche
                        if (axiosError.response.data.error.code === "goasync") {
                            return {
                                task_id: axiosError.response.data.data.task_id,
                            }
                        }
                    }
                    throw error
                }
            }
        )
    }

    /**
     * Lance la tâche d'export de toutes les données d'une organisation
     *
     * @param _id_company - Identifiant de l'organisation
     * @param freq - Fréquence des données (daily, weekly, monthly)
     * @param date_from - Date de début au format YYYY-MM-DD
     * @param date_to - Date de fin au format YYYY-MM-DD
     * @param variables - Liste des variables à exporter
     * @param aggregate - Agréger les données manquantes
     * @return L'identifiant de la tâche à suivre
     */
    async exportAllDataByCompanyId(
        _id_company: string,
        {
            freq,
            date_from,
            date_to,
            variables,
            aggregate,
            _id_user,
        }: {
            freq: string
            date_from: string
            date_to: string
            variables: string[]
            aggregate: boolean
            _id_user: string
        },
        headers: Record<string, string> = {}
    ): Promise<string> {
        return handleServiceCall(async () => {
            const params = {
                date_from,
                date_to,
                variables,
                _id_company,
                ignore_missing_values: aggregate,
                daily: freq === "daily",
                weekly: freq === "weekly",
                monthly: freq === "monthly",
            } as Record<string, unknown>

            if (_id_user) {
                params["_id_user"] = _id_user
            }

            const { data } = await axios.get(
                `/api/dapi/export/buildings_sources`,
                {
                    params,
                    paramsSerializer: paramsSerializer,
                    headers: {
                        "x-user-subscribe-id":
                            headers["x-user-subscribe-id"] || _id_user,
                        "x-run-async": true,
                    },
                    validateStatus: (status) => {
                        // Accepte les status 2xx et 303
                        return status < 300 || status === 303
                    },
                }
            )
            return data?.data?.task_id
        })
    }

    /**
     * Lance la tâche d'export du référentiel technique d'une organisation.
     *
     * @param _id_company - Identifiant de l'organisation
     * @param date_from - Date de début
     * @param date_to - Date de fin
     * @param aggregate - Agréger les données manquantes
     * @param _id_user - Identifiant de l'utilisateur
     *
     * @returns L'identifiant de la tâche à suivre
     */
    async exportRT(
        _id_company: string,
        {
            date_from,
            date_to,
            aggregate,
            _id_user,
        }: {
            date_from: string
            date_to: string
            aggregate: boolean
            _id_user: string | null
        } = {
            date_from: "",
            date_to: "",
            aggregate: false,
            _id_user: null,
        },
        headers: Record<string, string> = {}
    ): Promise<string> {
        return handleServiceCall(async () => {
            const params = {
                date_from,
                date_to,
                _id_company,
                ignore_missing_values: aggregate,
                _id_user,
            }

            const { data } = await axios.get(`/api/dapi/export/rt`, {
                params,
                paramsSerializer: paramsSerializer,
                headers: {
                    "x-user-subscribe-id":
                        headers["x-user-subscribe-id"] || _id_user,
                    "x-run-async": true,
                },
            })
            return data?.data?.task_id
        })
    }

    /**
     * Récupère la synthèse IoT pour le dashboard
     * @param folderId - Identifiant du dossier (optionnel)
     * @param controller - Contrôleur d'annulation pour la requête
     */
    async getDashboardIotAnalyse({
        folderId,
        controller,
    }: {
        folderId: string | null
        controller?: AbortController
    }): Promise<any> {
        return handleServiceCall(async () => {
            const params = folderId ? { _id_folder: folderId } : {}
            const { data } = await axios.get(
                `/api/metier/v5/energymanager/workflowanalyses/iot`,
                {
                    params,
                    signal: controller?.signal,
                }
            )
            return data
        })
    }

    /**
     * Crée une nouvelle synthèse IoT pour le dashboard
     * @param dateFrom - Date de début au format YYYY-MM-DD
     * @param dateTo - Date de fin au format YYYY-MM-DD
     * @param folderId - Identifiant du dossier (optionnel)
     * @param controller - Contrôleur d'annulation pour la requête
     */
    async createDashboardIotAnalyse({
        dateFrom,
        dateTo,
        folderId,
        controller,
    }: {
        dateFrom: string
        dateTo: string
        folderId: string | null
        controller?: AbortController
    }): Promise<any> {
        return handleServiceCall(async () => {
            const payload: Record<string, unknown> = {
                date_from: dateFrom,
                date_to: dateTo,
            }
            if (folderId) {
                payload._id_folder = folderId
            }
            const { data } = await axios.post(
                `/api/metier/v5/energymanager/workflowanalyses/iot`,
                payload,
                {
                    signal: controller?.signal,
                }
            )
            return data
        })
    }

    /**
     * Lance la tâche pour générer le rapport Excel d'une synthèse d'un dashboard.
     * L'event_name est généré côté front et envoyé à l'API pour que le backend
     * émette les mises à jour Pusher sur ce canal ; il est renvoyé pour followEvent.
     * @param analyseId - Identifiant du document workflowanalyses
     * @param controller - Contrôleur d'annulation pour la requête
     * @returns Objet contenant task_id et event_name pour suivre la tâche
     */
    async generateDashboardAnalyseReport({
        analyseId,
        controller,
    }: {
        analyseId: string
        controller?: AbortController
    }): Promise<{ task_id: string; event_name: string }> {
        return handleServiceCall(async () => {
            const event_name = `dashboard.iot.export_excel.${analyseId}.${crypto.randomUUID()}`

            const { data } = await axios.post(
                `/api/metier/v5/workflowanalyses/${analyseId}/excel`,
                { event_name },
                {
                    signal: controller?.signal,
                }
            )

            // Réponse API : { status, task_id, event_name }
            const taskId = data?.task_id ?? data?.data?.task_id ?? ""
            const returnedEventName =
                data?.event_name ?? data?.data?.event_name ?? event_name

            return {
                task_id: taskId,
                event_name: returnedEventName,
            }
        })
    }
}
